import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Checkbox, Input, Select, Typography, showToast } from '@attentive-platform/stem-ui';
import { FormContainer } from '../../styled';
import { fetchApiData } from '../../Utils/ApiCalls';
import { ENTERPRISE_INFO, UPDATE_ENTERPRISE, GET_CATEGORIES } from '../../urls';
import {
  CHECKBOX_FIELDS_BEAM_USER,
  COUNTRY_CODE,
  dfyModelToCommercialModel,
  isCommercialModelLicense,
  isCommercialModelUsage
} from '../../Constants/Constants';
import {
  addOneYearToDate,
  centsToDollar,
  dollarToCents,
  formatAutoCorrectionsAppliedMessage,
  needsSuspendedEarlyAcknowledgement,
  normalizeBillingFrequency,
  setContractInfoData,
  toDateOnly,
  transformTradeWisePricing,
  resolveTotalUsersFromEnterpriseInfo
} from '../../Utils/HelperFunctions';
import {
  BEAM_BILLING_FREQUENCY,
  BEAM_CATEGORY_PRICING_TYPES,
  DFY_MODEL_ITEMS,
  getEmptyBeamContractPricing,
  unwrapEnterpriseInfoPayload,
  mergeSharedContractTermsForForm,
  pickPlatformAccessFromEnterpriseInfo,
  resolveCompanyInfoSliceFromEnterpriseInfo,
  extractBillingDetailsFromEnterpriseInfo,
  coerceEnterpriseEmailList,
  toAllowedDiyAiTradeIds,
  fetchSopConfiguredForEmail,
  fetchCompanyScopesKamEmail,
  preferExistingKamOrScope
} from '../../Utils/beamEnterpriseShared';
import { CompanyInfoSection, ContractTermsSection, BillingInfoSection } from '../SharedComponent';
import DIYEntitlementSection from './DIYEntitlementSection';
import MultiTradeSection from './MultiTradeSection';
import InfoBanner from './InfoBanner';
import {
  CardWrap,
  LookupRow,
  CompanyBadge,
  TilesGrid,
  TileBox,
  TileLabel,
  TileValue,
  TileSub,
  SectionBlock,
  SectionTitle,
  BannerBox,
  BannerIcon,
  FormDivider,
  FieldWrap,
  FieldLabel,
  HelpNote,
  SaveRow,
  CheckboxRow,
  ReadOnlyNote
} from './styles';

const formatDisplayDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const SnapshotTile = ({ label, value, sub, variant }) => (
  <TileBox variant={variant}>
    <TileLabel>{label}</TileLabel>
    <TileValue>{value || '—'}</TileValue>
    {sub && <TileSub>{sub}</TileSub>}
  </TileBox>
);

const formatBeamCheckboxLabel = field => field.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const CounterGridTileValue = ({ children, warn }) => (
  <TileValue style={{ fontSize: '1.375rem', ...(warn !== undefined ? { color: warn ? '#c53030' : '#276749' } : {}) }}>
    {children}
  </TileValue>
);

/** DFY license-based job usage snapshot (not editable here). */
const DfyLicenseCounterGrid = ({ licenseCounters }) => {
  const { jobCap, used, remaining, periodResets } = licenseCounters;
  const overdrawn = remaining <= 0;
  return (
    <TilesGrid style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
      <TileBox>
        <TileLabel>Job cap (current period)</TileLabel>
        <CounterGridTileValue>{jobCap}</CounterGridTileValue>
      </TileBox>
      <TileBox>
        <TileLabel>Used</TileLabel>
        <CounterGridTileValue>{used}</CounterGridTileValue>
      </TileBox>
      <TileBox variant={overdrawn ? 'warn' : 'good'}>
        <TileLabel>Remaining</TileLabel>
        <CounterGridTileValue warn={overdrawn}>{Math.abs(remaining)}</CounterGridTileValue>
        {remaining < 0 && <TileSub>Overage</TileSub>}
      </TileBox>
      <TileBox>
        <TileLabel>Period resets</TileLabel>
        <TileValue>{periodResets}</TileValue>
      </TileBox>
    </TilesGrid>
  );
};

const Banner = ({ variant, children }) => (
  <BannerBox variant={variant}>
    <BannerIcon>{variant === 'info' ? 'i' : '!'}</BannerIcon>
    <div>{children}</div>
  </BannerBox>
);

const UpdateEnterpriseContractBeam = () => {
  const [email, setEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [companyName, setCompanyName] = useState('');

  const [snapshot, setSnapshot] = useState(null);
  const [licenseCounters, setLicenseCounters] = useState(null);

  const [dfyModel, setDfyModel] = useState('license_based');
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [switchConfirmed, setSwitchConfirmed] = useState(false);
  const [acknowledgeSuspendedEarly, setAcknowledgeSuspendedEarly] = useState(false);

  const [dfyJobCap, setDfyJobCap] = useState('');
  const [tierSchedule, setTierSchedule] = useState([]);
  const [showTierEditor, setShowTierEditor] = useState(false);

  const [contractPricing, setContractPricing] = useState(() => getEmptyBeamContractPricing());

  const [dfyConcurrency, setDfyConcurrency] = useState('');

  const [diyForm, setDiyForm] = useState({ diyEnabled: false, diyJobCap: '', diyUsed: 0, allowedTrades: [] });

  const [checkboxes, setCheckboxes] = useState({
    apply_role_level_restriction: false,
    ignore_concurrency_limit: false,
    ignore_draft_limit: false,
    ignore_marketing_mailer: false,
    is_pilot: false
  });
  const [billingType, setBillingType] = useState('manual');

  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [saveLoading, setSaveLoading] = useState(false);
  const [contractInfo, setContractInfo] = useState({});
  const [isFsSynced, setIsFsSynced] = useState(false);
  const [sopConfigured, setSopConfigured] = useState(false);
  /** When null/undefined, API did not set `summary_tiles.dfy_model` — show editable DFY model selector (convert-style). */
  const [summaryTilesDfyModel, setSummaryTilesDfyModel] = useState(null);

  // Pass-through state: fields merged into update_enterprise payload
  const [passthrough, setPassthrough] = useState({
    total_users: '',
    company_vertical: '',
    account_segment: '',
    key_account_manager: [],
    onboarding_specialist: [],
    platform_access: { start_date: '', end_date: '' },
    billing_details: {},
    ea_extra: { concurrency_value: 0, subdomain: '', callback_url: '' },
    ec_extra: { start_date: '', end_date: '', sheet_cost: 0 }
  });

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await fetchApiData({ endpoint: GET_CATEGORIES, apiRequest: 'GET' });
      setCategories(data || []);
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    if (dataLoaded && dfyModel === 'usage_based') {
      fetchCategories();
    }
  }, [dataLoaded, dfyModel, fetchCategories]);

  const needsSuspendedEarlyAck = useMemo(
    () => needsSuspendedEarlyAcknowledgement(passthrough.platform_access?.end_date, passthrough.ec_extra?.end_date),
    [passthrough.platform_access?.end_date, passthrough.ec_extra?.end_date]
  );

  useEffect(() => {
    if (!needsSuspendedEarlyAck) setAcknowledgeSuspendedEarly(false);
  }, [needsSuspendedEarlyAck]);

  useEffect(() => {
    if (!dataLoaded || !contractInfo || Object.keys(contractInfo).length === 0) return;

    setPassthrough(prev => ({
      ...prev,
      billing_details: {
        ...prev.billing_details,
        name: contractInfo.company_name ?? '',
        email: contractInfo.billing_email ?? '',
        address: contractInfo.address ?? '',
        city: contractInfo.city ?? '',
        zip: contractInfo.zip ?? '',
        country: contractInfo.country ?? prev.billing_details?.country ?? COUNTRY_CODE.USA
      },
      platform_access: {
        start_date: contractInfo.platformAccessStartDate || '',
        end_date: contractInfo.platformAccessDate || ''
      },
      ec_extra: {
        ...prev.ec_extra,
        start_date: contractInfo.start_date || '',
        end_date: contractInfo.end_date || ''
      }
    }));

    setContractPricing(prev => ({
      ...prev,
      billing_frequency: normalizeBillingFrequency(contractInfo.billing_frequency)
    }));
  }, [contractInfo, dataLoaded]);

  const handleCompanyFieldChange = useCallback(
    (valueKey, value) => {
      if (isFsSynced) return;
      if (valueKey === 'key_account_manager') {
        const trimmed = (value ?? '').trim().toLowerCase();
        setPassthrough(prev => ({ ...prev, key_account_manager: trimmed ? [trimmed] : [] }));
        return;
      }
      if (valueKey === 'onboarding_specialist') {
        const trimmed = (value ?? '').trim().toLowerCase();
        setPassthrough(prev => ({ ...prev, onboarding_specialist: trimmed ? [trimmed] : [] }));
        return;
      }
      if (valueKey === 'company_vertical') {
        setPassthrough(prev => ({ ...prev, company_vertical: value }));
        return;
      }
      if (valueKey === 'account_segment') {
        setPassthrough(prev => ({ ...prev, account_segment: value }));
      }
    },
    [isFsSynced]
  );

  const handleSharedContractDataChange = useCallback(
    (val, field) => {
      if (isFsSynced) return;
      setContractInfo(prev => ({ ...prev, [field]: val }));
    },
    [isFsSynced]
  );

  const setSharedContractDates = useCallback(
    date => {
      if (isFsSynced) return;
      if (!date || String(date).length < 10) {
        setContractInfo(prev => ({ ...prev, start_date: date || '', end_date: '' }));
        return;
      }
      setContractInfo(prev => ({ ...prev, start_date: date, end_date: addOneYearToDate(date) }));
    },
    [isFsSynced]
  );

  const setContractInfoSafe = useCallback(
    updater => {
      if (isFsSynced) return;
      setContractInfo(updater);
    },
    [isFsSynced]
  );

  const handleLookup = async () => {
    if (!email) {
      showToast('Please enter a company email', 'error');
      return;
    }
    setLookupLoading(true);
    try {
      const response = await fetchApiData({
        endpoint: `${ENTERPRISE_INFO}?email=${encodeURIComponent(email.toLowerCase())}`,
        apiRequest: 'GET'
      });

      if (response.error) {
        showToast(response.error || 'Failed to fetch enterprise details', 'error');
        return;
      }

      const data = unwrapEnterpriseInfoPayload(response.data);

      const cs = data?.commercial_setup ?? data?.commercialSetup ?? {};
      const ubp = data?.usage_based_pricing ?? data?.usageBasedPricing ?? {};
      const ec = data?.enterprise_contract ?? data?.enterpriseContract ?? {};
      const lc = data?.license_counters ?? data?.licenseCounters ?? {};
      const st = data?.summary_tiles ?? data?.summaryTiles ?? {};
      const sct = mergeSharedContractTermsForForm(data);
      const platformAccessPick = pickPlatformAccessFromEnterpriseInfo(data);

      const ea = data?.enterprise_attributes ?? data?.enterpriseAttributes ?? {};

      const isLicenseBased =
        cs.commercial_model !== undefined && cs.commercial_model !== null && cs.commercial_model !== ''
          ? isCommercialModelLicense(cs.commercial_model)
          : false;

      setCompanyName(data?.company?.name || data?.billing_details?.name || email);

      const contractEndDate = sct.contract_end_date || '';
      const platformEndDate = sct.platform_access_end_date || '';
      const contractStatus =
        st.contract_status ||
        (platformEndDate && new Date(platformEndDate) < new Date()
          ? 'EXPIRED'
          : contractEndDate && new Date(contractEndDate) > new Date()
            ? 'ACTIVE'
            : '—');
      const isActive = contractStatus === 'ACTIVE';

      const diyOn = cs.diy_enabled ?? data?.diy_enabled ?? false;
      const aiTradesRaw = cs.allowed_diy_ai_trades ?? data?.allowed_diy_ai_trades ?? [];
      const aiTrades = aiTradesRaw.map(t =>
        t && typeof t === 'object'
          ? { id: t.id ?? t.value, name: t.name ?? t.display_name ?? '' }
          : { id: t, name: '' }
      );
      const diyStatus = st.diy_status || (diyOn ? 'ENABLED' : 'DISABLED');
      const diyTradesSub = diyOn ? `${aiTrades.length} trade${aiTrades.length !== 1 ? 's' : ''}, manual on` : '—';

      setSnapshot({
        companyTier: st.company_tier || (ea.is_pilot ? 'Pilot Enterprise' : 'Enterprise'),
        companyTierSub: data?.variant?.is_fs_synced ? 'FS-synced (csm.attentive)' : 'Beam-native',
        dfyModel: st.dfy_model
          ? isCommercialModelLicense(st.dfy_model)
            ? 'License-based'
            : 'Usage-based'
          : isLicenseBased
            ? 'License-based'
            : 'Usage-based',
        dfyModelSub: isLicenseBased ? 'Hard-blocking on exhaustion' : 'Usage-based billing',
        contractStatus: isActive ? 'Active' : contractEndDate ? 'Expired' : '—',
        contractStatusVariant: isActive ? 'good' : contractEndDate ? 'warn' : '',
        contractStatusSub: contractEndDate ? `Ends ${formatDisplayDate(contractEndDate)}` : '',
        platformAccessEnd: platformEndDate ? formatDisplayDate(platformEndDate) : '—',
        platformAccessSub: 'Synced with contract end',
        diyStatus: diyStatus === 'ENABLED' ? 'Enabled' : diyStatus === 'EXHAUSTED' ? 'Exhausted' : 'Disabled',
        diyStatusSub: diyTradesSub,
        phase2Status: st.phase_2_complete ? 'Complete' : 'Incomplete',
        phase2Sub: 'SOP + commercial model configured',
        phase2Variant: st.phase_2_complete ? '' : 'warn'
      });

      if (isLicenseBased) {
        const cap = lc.dfy_job_cap ?? cs.dfy_job_cap ?? 0;
        const used = lc.dfy_units_used ?? 0;
        const remaining = lc.dfy_units_remaining ?? cap - used;
        setLicenseCounters({
          jobCap: cap,
          used,
          remaining,
          diyJobCap: lc.diy_job_cap ?? cs.diy_job_cap ?? 0,
          diyUsed: lc.diy_jobs_used ?? 0,
          diyRemaining: lc.diy_jobs_remaining ?? 0,
          periodResets: lc.period_resets_on ? formatDisplayDate(lc.period_resets_on) : '—'
        });
      } else {
        setLicenseCounters(null);
      }

      setDfyModel(isLicenseBased ? 'license_based' : 'usage_based');
      setDfyJobCap(cs.dfy_job_cap ?? data?.dfy_job_cap ?? '');
      const rawTiers = cs.dfy_tier_schedule ?? data?.dfy_tier_schedule ?? [];
      setTierSchedule(
        rawTiers.map(row => ({
          max_sheets: row?.max_sheets ?? row?.threshold ?? '',
          units: row?.units ?? ''
        }))
      );

      const pickPricing = key => {
        const v = ec[key] ?? ubp[key];
        return v != null && v !== '' ? centsToDollar(v) : '';
      };
      const twp = ec.trade_wise_pricing ?? ubp.trade_wise_pricing ?? {};
      const twpInDollars = Object.keys(twp).length ? transformTradeWisePricing(twp, 'centsToDollar') : null;
      setContractPricing({
        bpt_contract_amount: pickPricing('bpt_contract_amount'),
        workable_sheet_cost: pickPricing('workable_sheet_cost'),
        addendum_sheet_cost: pickPricing('addendum_sheet_cost'),
        manual_worksheet_cost: pickPricing('manual_worksheet_cost'),
        billing_frequency: ec.billing_frequency ?? ubp.billing_frequency ?? BEAM_BILLING_FREQUENCY.QUARTERLY,
        trade_wise_pricing: twpInDollars ?? {
          others: {
            [BEAM_CATEGORY_PRICING_TYPES.FRESH]: 0,
            [BEAM_CATEGORY_PRICING_TYPES.ADDENDUM]: 0
          }
        }
      });

      setDfyConcurrency(cs.dfy_concurrency_limit ?? ea.concurrency_value ?? '');

      setDiyForm({
        diyEnabled: diyOn,
        diyJobCap: cs.diy_job_cap ?? data?.diy_job_cap ?? '',
        diyUsed: lc.diy_jobs_used ?? 0,
        allowedTrades: aiTrades
      });

      setCheckboxes({
        apply_role_level_restriction: ea.apply_role_level_restriction ?? false,
        ignore_concurrency_limit: ea.ignore_concurrency_limit ?? false,
        ignore_draft_limit: ea.ignore_draft_limit ?? false,
        ignore_marketing_mailer: ea.ignore_marketing_mailer ?? false,
        is_pilot: ea.is_pilot ?? false
      });

      setSelectedCategories(Object.keys(twp).filter(k => k !== 'others'));
      setBillingType(ea.billing_type || 'manual');

      const ci = resolveCompanyInfoSliceFromEnterpriseInfo(data);
      const billingNormalized = extractBillingDetailsFromEnterpriseInfo(data);
      const resolvedTotalUsers = resolveTotalUsersFromEnterpriseInfo(data, sct);
      const scopeKamEmail = await fetchCompanyScopesKamEmail(email);
      const infoKam = coerceEnterpriseEmailList(ci.key_account_managers).slice(0, 1);
      const resolvedKam = preferExistingKamOrScope(infoKam[0], scopeKamEmail);
      setPassthrough({
        total_users: resolvedTotalUsers,
        company_vertical: ci.business_unit ?? '',
        account_segment: ci.account_segment ?? '',
        key_account_manager: resolvedKam ? [resolvedKam] : [],
        onboarding_specialist: coerceEnterpriseEmailList(ci.onboarding_specialist).slice(0, 1),
        platform_access: {
          start_date: toDateOnly(platformAccessPick.start_date ?? ''),
          end_date: toDateOnly(sct.platform_access_end_date ?? platformAccessPick.end_date ?? '')
        },
        billing_details: billingNormalized,
        ea_extra: {
          concurrency_value: cs.dfy_concurrency_limit ?? ea.concurrency_value ?? 0,
          subdomain: ea.subdomain ?? '',
          callback_url: ea.callback_url ?? ''
        },
        ec_extra: {
          start_date: toDateOnly(sct.contract_start_date ?? ''),
          end_date: toDateOnly(sct.contract_end_date ?? ''),
          sheet_cost: data.enterprise_contract?.sheet_cost ?? data.enterpriseContract?.sheet_cost ?? 0
        }
      });

      const twpMerged = Object.keys(twp).length
        ? twp
        : {
            others: {
              [BEAM_CATEGORY_PRICING_TYPES.FRESH]: 0,
              [BEAM_CATEGORY_PRICING_TYPES.ADDENDUM]: 0
            }
          };

      const toEnterpriseIso = raw => {
        const s = toDateOnly(String(raw || ''));
        return s ? `${s}T00:00:00+05:30` : '';
      };

      setContractInfo(
        setContractInfoData({
          enterpriseContract: {
            start_date: toEnterpriseIso(
              sct.contract_start_date || data?.enterprise_contract?.start_date || data?.enterpriseContract?.start_date
            ),
            end_date: toEnterpriseIso(
              sct.contract_end_date || data?.enterprise_contract?.end_date || data?.enterpriseContract?.end_date
            ),
            billing_frequency: ec.billing_frequency ?? ubp.billing_frequency ?? BEAM_BILLING_FREQUENCY.QUARTERLY,
            bpt_contract_amount: ec.bpt_contract_amount ?? ubp.bpt_contract_amount ?? '',
            workable_sheet_cost: ec.workable_sheet_cost ?? ubp.workable_sheet_cost ?? '',
            addendum_sheet_cost: ec.addendum_sheet_cost ?? ubp.addendum_sheet_cost ?? '',
            manual_worksheet_cost: ec.manual_worksheet_cost ?? ubp.manual_worksheet_cost ?? '',
            trade_wise_pricing: twpMerged
          },
          billingDetails: billingNormalized,
          platformAccess: {
            ...(toDateOnly(platformAccessPick.start_date ?? '') && {
              start_date: toEnterpriseIso(platformAccessPick.start_date)
            }),
            ...(toDateOnly(sct.platform_access_end_date ?? platformAccessPick.end_date ?? '') && {
              end_date: toEnterpriseIso(sct.platform_access_end_date ?? platformAccessPick.end_date)
            })
          }
        })
      );
      setIsFsSynced(Boolean(data?.variant?.is_fs_synced));
      const serverConfigured = Boolean(data?.sop_status?.is_configured);
      setSopConfigured(await fetchSopConfiguredForEmail(email, serverConfigured));
      setSummaryTilesDfyModel(st.dfy_model ?? null);

      setDataLoaded(true);
    } catch {
      showToast('Failed to fetch enterprise details', 'error');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleClear = () => {
    setEmail('');
    setDataLoaded(false);
    setCompanyName('');
    setSnapshot(null);
    setLicenseCounters(null);
    setDfyModel('license_based');
    setShowSwitchConfirm(false);
    setSwitchConfirmed(false);
    setAcknowledgeSuspendedEarly(false);
    setDfyJobCap('');
    setTierSchedule([]);
    setShowTierEditor(false);
    setDfyConcurrency('');
    setDiyForm({ diyEnabled: false, diyJobCap: '', diyUsed: 0, allowedTrades: [] });
    setCheckboxes({
      apply_role_level_restriction: false,
      ignore_concurrency_limit: false,
      ignore_draft_limit: false,
      ignore_marketing_mailer: false,
      is_pilot: false
    });
    setBillingType('manual');
    setCategories([]);
    setSelectedCategories([]);
    setContractPricing(getEmptyBeamContractPricing());
    setContractInfo({});
    setIsFsSynced(false);
    setSopConfigured(false);
    setSummaryTilesDfyModel(null);
    setPassthrough({
      total_users: '',
      company_vertical: '',
      account_segment: '',
      key_account_manager: [],
      onboarding_specialist: [],
      platform_access: { start_date: '', end_date: '' },
      billing_details: {},
      ea_extra: { concurrency_value: 0, subdomain: '', callback_url: '' },
      ec_extra: { start_date: '', end_date: '', sheet_cost: 0 }
    });
  };

  const handleSwitchConfirm = () => {
    setDfyModel('license_based');
    setLicenseCounters(null);
    setSwitchConfirmed(true);
    setShowSwitchConfirm(false);
  };

  const handleDfyModelChange = val => {
    if (dfyModel === 'usage_based' && val === 'license_based') {
      setShowSwitchConfirm(true);
      return;
    }
    if (val === 'usage_based') {
      setSwitchConfirmed(false);
    }
    setDfyModel(val);
  };

  const addTierRow = () => {
    setTierSchedule(prev => [...prev, { max_sheets: '', units: '' }]);
  };

  const updateTierRow = (index, field, value) => {
    setTierSchedule(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeTierRow = index => {
    setTierSchedule(prev => prev.filter((_, i) => i !== index));
  };

  const diyRemaining = Math.max(0, (Number(diyForm.diyJobCap) || 0) - (diyForm.diyUsed || 0));

  const handleTradeToggle = trade => {
    setDiyForm(prev => ({
      ...prev,
      allowedTrades: prev.allowedTrades.some(t => t.id === trade.id)
        ? prev.allowedTrades.filter(t => t.id !== trade.id)
        : [...prev.allowedTrades, trade]
    }));
  };

  const handleCategoryPricingChange = (categoryId, type, value) => {
    setContractPricing(prev => ({
      ...prev,
      trade_wise_pricing: {
        ...prev.trade_wise_pricing,
        [categoryId]: { ...prev.trade_wise_pricing?.[categoryId], [type]: value }
      }
    }));
  };

  const handleCategorySelect = e => {
    const categoryId = e.target.value;
    if (!categoryId || selectedCategories.includes(categoryId)) return;
    setSelectedCategories(prev => [...prev, categoryId]);
    setContractPricing(prev => ({
      ...prev,
      trade_wise_pricing: {
        ...prev.trade_wise_pricing,
        [categoryId]: {
          [BEAM_CATEGORY_PRICING_TYPES.FRESH]: '',
          [BEAM_CATEGORY_PRICING_TYPES.ADDENDUM]: ''
        }
      }
    }));
  };

  const handleDeleteCategory = categoryId => {
    setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    setContractPricing(prev => {
      const updated = { ...prev.trade_wise_pricing };
      delete updated[categoryId];
      return { ...prev, trade_wise_pricing: updated };
    });
  };

  const validateTierSchedule = schedule => {
    for (let i = 0; i < schedule.length; i++) {
      const { max_sheets, units } = schedule[i];
      if (max_sheets === '' || units === '') return false;
      if (i > 0) {
        if (Number(max_sheets) <= Number(schedule[i - 1].max_sheets)) return false;
        if (Number(units) < Number(schedule[i - 1].units)) return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (dfyModel === 'license_based' && (dfyJobCap === '' || Number(dfyJobCap) < 0)) {
      showToast('Please enter a valid DFY Job Cap', 'error');
      return;
    }
    if (tierSchedule.length > 0 && !validateTierSchedule(tierSchedule)) {
      showToast('Tier schedule: thresholds must be strictly increasing, units must be non-decreasing', 'error');
      return;
    }
    if (diyForm.diyEnabled && (diyForm.diyJobCap === '' || Number(diyForm.diyJobCap) < 0)) {
      showToast('Please enter a valid DIY Job Cap', 'error');
      return;
    }
    if (needsSuspendedEarlyAck && !acknowledgeSuspendedEarly) {
      showToast(
        'Platform access ends before the contract end. Confirm suspended-early access below, then save again (AC-160).',
        'error'
      );
      return;
    }
    if (!sopConfigured) {
      showToast(
        'SOP must be configured before you can save. Configure SOP in csm.app.ibeam.ai, then try again.',
        'error'
      );
      return;
    }

    setSaveLoading(true);
    try {
      const commercial_setup = {
        commercial_model: dfyModelToCommercialModel(dfyModel),
        confirm_switch_to_license: switchConfirmed,
        dfy_concurrency_limit: Number(dfyConcurrency) || 0,
        diy_enabled: diyForm.diyEnabled,
        diy_job_cap: diyForm.diyEnabled ? Number(diyForm.diyJobCap) : null,
        allowed_diy_ai_trades: diyForm.diyEnabled ? toAllowedDiyAiTradeIds(diyForm.allowedTrades) : []
      };

      if (dfyModel === 'license_based') {
        commercial_setup.dfy_job_cap = Number(dfyJobCap);
        commercial_setup.dfy_tier_schedule = tierSchedule.map(row => ({
          threshold: Number(row.max_sheets),
          units: Number(row.units)
        }));
      }

      const bd = passthrough.billing_details;
      const toEnterpriseIso = d => (d ? `${d}T00:00:00+05:30` : '');
      const payload = {
        acknowledge_suspended_early: Boolean(needsSuspendedEarlyAck && acknowledgeSuspendedEarly),
        email: email.toLowerCase(),
        ...(passthrough.total_users !== '' && { total_users: passthrough.total_users }),
        ...(passthrough.company_vertical && { company_vertical: passthrough.company_vertical }),
        ...(passthrough.account_segment && { account_segment: passthrough.account_segment }),
        // FS-synced company section is read-only — never commit a substituted KAM on unrelated edits.
        ...(!isFsSynced &&
          passthrough.key_account_manager?.length && { key_account_manager: passthrough.key_account_manager }),
        ...(passthrough.onboarding_specialist?.length && { onboarding_specialist: passthrough.onboarding_specialist }),
        platform_access: {
          ...(passthrough.platform_access.start_date && { start_date: passthrough.platform_access.start_date }),
          ...(passthrough.platform_access.end_date && { end_date: passthrough.platform_access.end_date })
        },
        ...((bd?.name || bd?.email) && { billing_details: bd }),
        enterprise_attributes: {
          ...checkboxes,
          billing_type: billingType,
          ...passthrough.ea_extra
        },
        commercial_setup,
        enterprise_contract: {
          ...(passthrough.ec_extra.start_date && { start_date: toEnterpriseIso(passthrough.ec_extra.start_date) }),
          ...(passthrough.ec_extra.end_date && { end_date: toEnterpriseIso(passthrough.ec_extra.end_date) })
        }
      };

      if (dfyModel === 'usage_based') {
        payload.enterprise_contract = {
          ...payload.enterprise_contract,
          sheet_cost: passthrough.ec_extra.sheet_cost,
          bpt_contract_amount: dollarToCents(contractPricing.bpt_contract_amount),
          workable_sheet_cost: dollarToCents(contractPricing.workable_sheet_cost),
          addendum_sheet_cost: dollarToCents(contractPricing.addendum_sheet_cost),
          manual_worksheet_cost: dollarToCents(contractPricing.manual_worksheet_cost),
          billing_frequency: normalizeBillingFrequency(contractPricing.billing_frequency),
          trade_wise_pricing: Object.fromEntries(
            Object.entries(contractPricing.trade_wise_pricing || {}).map(([key, value]) => [
              key,
              {
                [BEAM_CATEGORY_PRICING_TYPES.FRESH]: dollarToCents(value[BEAM_CATEGORY_PRICING_TYPES.FRESH]),
                [BEAM_CATEGORY_PRICING_TYPES.ADDENDUM]: dollarToCents(value[BEAM_CATEGORY_PRICING_TYPES.ADDENDUM])
              }
            ])
          )
        };
      }

      const response = await fetchApiData({ endpoint: UPDATE_ENTERPRISE, data: payload });

      if (response.error) {
        const errStr = String(response.error);
        if (errStr.includes('SUSPENDED_EARLY')) {
          showToast(
            'Platform access ends before the contract end. Confirm suspended-early access and save again (AC-160).',
            'error'
          );
        } else {
          showToast(response.error || 'Failed to save changes', 'error');
        }
        return;
      }

      const { summary_tiles: st, phase_2_complete, variant: responseVariant } = response.data ?? {};
      if (responseVariant != null && 'is_sop_configured' in responseVariant) {
        setSopConfigured(Boolean(responseVariant.is_sop_configured));
      }
      if (st) {
        setSnapshot(prev => ({
          ...prev,
          phase2Status: phase_2_complete ? 'Complete' : 'Incomplete',
          phase2Sub: 'SOP + commercial model configured',
          phase2Variant: phase_2_complete ? '' : 'warn',
          dfyModel: isCommercialModelLicense(st.dfy_model)
            ? 'License-based'
            : isCommercialModelUsage(st.dfy_model)
              ? 'Usage-based'
              : prev.dfyModel,
          diyStatus: st.diy_status === 'ENABLED' ? 'Enabled' : st.diy_status === 'EXHAUSTED' ? 'Exhausted' : 'Disabled'
        }));
        if ('dfy_model' in st) {
          setSummaryTilesDfyModel(st.dfy_model ?? null);
        }
      }
      setSwitchConfirmed(false);
      setAcknowledgeSuspendedEarly(false);
      const correctionMsg = formatAutoCorrectionsAppliedMessage(response.data?.auto_corrections_applied);
      if (correctionMsg) {
        showToast(correctionMsg, 'info');
      }
      showToast('Contract updated successfully', 'success');
    } catch {
      showToast('Failed to save changes', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <FormContainer>
      <CardWrap>
        {/* Email lookup */}
        <LookupRow>
          <Input
            value={email}
            disabled={dataLoaded}
            placeholder='Enter admin email'
            onChange={e => setEmail(e.target.value)}
            width='100%'
          />
          <Button
            color='primary'
            variant='contained'
            loading={lookupLoading}
            onClick={dataLoaded ? handleClear : handleLookup}>
            {dataLoaded ? 'Clear' : 'Next'}
          </Button>
        </LookupRow>

        {dataLoaded && (
          <>
            <CompanyBadge>Company: {companyName}</CompanyBadge>

            {/* Contract snapshot tiles */}
            <SectionBlock>
              <SectionTitle>Current contract snapshot </SectionTitle>
              <TilesGrid>
                <SnapshotTile label='Company tier' value={snapshot?.companyTier} sub={snapshot?.companyTierSub} />
                <SnapshotTile label='DFY model' value={snapshot?.dfyModel} sub={snapshot?.dfyModelSub} />
                <SnapshotTile
                  label='Contract status'
                  value={snapshot?.contractStatus}
                  sub={snapshot?.contractStatusSub}
                  variant={snapshot?.contractStatusVariant}
                />
                <SnapshotTile
                  label='Platform access ends'
                  value={snapshot?.platformAccessEnd}
                  sub={snapshot?.platformAccessSub}
                />
                <SnapshotTile label='DIY' value={snapshot?.diyStatus} sub={snapshot?.diyStatusSub} />
                <SnapshotTile
                  label='Phase 2 status'
                  value={snapshot?.phase2Status}
                  sub={snapshot?.phase2Sub}
                  variant={snapshot?.phase2Variant}
                />
              </TilesGrid>
            </SectionBlock>

            <SectionBlock>
              {isFsSynced ? (
                <InfoBanner variant='purple' icon='⤵'>
                  <strong>Synced from csm.app.attentive.ai.</strong> Company info, contract dates, seats, and billing
                  info are read-only here. To edit them, go to csm.app.attentive.ai → Update Enterprise Contract.
                </InfoBanner>
              ) : (
                <InfoBanner variant='info' icon='ℹ'>
                  You can edit company info, contract terms, and billing here.
                </InfoBanner>
              )}
              <SectionTitle>Company, billing & shared terms</SectionTitle>
              <CompanyInfoSection
                data={{
                  company_vertical: passthrough.company_vertical ?? '',
                  account_segment: passthrough.account_segment ?? '',
                  key_account_manager: Array.isArray(passthrough.key_account_manager)
                    ? (passthrough.key_account_manager[0] ?? '')
                    : (passthrough.key_account_manager ?? ''),
                  onboarding_specialist: Array.isArray(passthrough.onboarding_specialist)
                    ? (passthrough.onboarding_specialist[0] ?? '')
                    : (passthrough.onboarding_specialist ?? '')
                }}
                readOnly={isFsSynced}
                onFieldChange={handleCompanyFieldChange}
              />
              <BillingInfoSection
                companyName={contractInfo.company_name ?? ''}
                billingEmail={contractInfo.billing_email ?? ''}
                address={contractInfo.address ?? ''}
                city={contractInfo.city ?? ''}
                zipcode={contractInfo.zip ?? ''}
                country={contractInfo.country ?? COUNTRY_CODE.USA}
                handleDataChange={handleSharedContractDataChange}
                readOnly={isFsSynced}
              />
              <ContractTermsSection
                contractInfo={contractInfo}
                setContractInfo={setContractInfoSafe}
                handleDataChange={handleSharedContractDataChange}
                setDates={setSharedContractDates}
                readOnly={isFsSynced}
                totalUsers={passthrough.total_users ?? ''}
                onTotalUsersChange={val => setPassthrough(prev => ({ ...prev, total_users: val }))}
              />
            </SectionBlock>

            <FormDivider />

            {/* License counters — shown only when DFY is license-based */}
            {dfyModel === 'license_based' && licenseCounters && (
              <SectionBlock>
                <SectionTitle>DFY license counters</SectionTitle>
                <DfyLicenseCounterGrid licenseCounters={licenseCounters} />

                <ReadOnlyNote>
                  Counters come from job submissions and reset on contract anniversary. DIY usage can be reset from the
                  DIY configuration section below (DFY counters reset on renewal).
                </ReadOnlyNote>
              </SectionBlock>
            )}

            <FormDivider />

            <SectionBlock>
              <SectionTitle>DFY scope / SOP</SectionTitle>
              <Typography variant='body4' color='var(--color-neutral-60)' style={{ fontSize: '12.5px' }}>
                Manage SOP at csm.app.ibeam.ai → SOP Management.
              </Typography>
              {sopConfigured ? (
                <InfoBanner variant='new' icon='✓'>
                  SOP is configured. You can save contract changes here.
                </InfoBanner>
              ) : (
                <InfoBanner variant='warn' icon='!'>
                  <strong>SOP must be configured before you can save.</strong> Open csm.app.ibeam.ai → SOP Management,
                  configure trades and scope items, then return to this form.
                </InfoBanner>
              )}
            </SectionBlock>

            <FormDivider />

            {/* Multi-Trade Commercial Setup */}
            <SectionBlock>
              <SectionTitle>Multi-Trade Commercial Setup</SectionTitle>

              {summaryTilesDfyModel == null ? (
                <>
                  <InfoBanner variant='new' icon='+'>
                    This section determines the DFY model. The selection drives which pricing/cap fields are required.
                    DIY is configured independently below.
                  </InfoBanner>
                  <FieldWrap>
                    <FieldLabel>
                      DFY contract model <span style={{ color: '#e53e3e' }}>*</span>
                    </FieldLabel>
                    <Select
                      size='large'
                      color='secondary'
                      items={DFY_MODEL_ITEMS}
                      value={dfyModel}
                      onChange={handleDfyModelChange}
                      placeholder='Select DFY model...'
                      variant='std-filled'
                    />
                    <HelpNote>
                      Usage-based is priced per chargeable sheet (existing behavior). License-based uses a job-cap with
                      hard-blocking when exhausted.
                    </HelpNote>
                  </FieldWrap>
                </>
              ) : (
                <>
                  <FieldWrap>
                    <FieldLabel>
                      DFY contract model <span style={{ color: '#e53e3e' }}>*</span>
                    </FieldLabel>
                    <select
                      disabled
                      className='form-select form-select-sm'
                      style={{ background: '#f6f8fa', maxWidth: '340px' }}>
                      <option>
                        {dfyModel === 'license_based' ? 'DFY license-based (current)' : 'DFY usage-based (current)'}
                      </option>
                    </select>
                    <HelpNote>
                      To change the model, use the &quot;Switch DFY model&quot; action below. Direct editing is disabled
                      to prevent accidental commercial changes.
                    </HelpNote>
                  </FieldWrap>

                  <Banner variant='warn'>
                    <strong>Switching is one-way (v1).</strong> Usage-based → license-based is supported. License-based
                    → usage-based is not supported in v1.
                  </Banner>

                  {dfyModel === 'usage_based' ? (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        type='button'
                        className='btn btn-outline-secondary btn-sm'
                        onClick={() => setShowSwitchConfirm(true)}>
                        Switch DFY model →
                      </button>
                      <span style={{ fontSize: '11.5px', color: '#718096' }}>
                        Reveals confirmation before applying.
                      </span>
                    </div>
                  ) : (
                    <HelpNote style={{ marginTop: '4px' }}>
                      Already on license-based. Switching back to usage-based is not supported in v1.
                    </HelpNote>
                  )}
                </>
              )}

              <MultiTradeSection
                dfyModel={dfyModel}
                showSwitchConfirm={showSwitchConfirm}
                setShowSwitchConfirm={setShowSwitchConfirm}
                handleSwitchConfirm={handleSwitchConfirm}
                dfyJobCap={dfyJobCap}
                setDfyJobCap={setDfyJobCap}
                tierSchedule={tierSchedule}
                setTierSchedule={setTierSchedule}
                showTierEditor={showTierEditor}
                setShowTierEditor={setShowTierEditor}
                addTierRow={addTierRow}
                updateTierRow={updateTierRow}
                removeTierRow={removeTierRow}
                contractPricing={contractPricing}
                setContractPricing={setContractPricing}
                categories={categories}
                selectedCategories={selectedCategories}
                handleCategorySelect={handleCategorySelect}
                handleCategoryPricingChange={handleCategoryPricingChange}
                handleDeleteCategory={handleDeleteCategory}
              />
            </SectionBlock>

            <FormDivider />

            {/* Construction concurrency */}
            <SectionBlock>
              <SectionTitle>Construction concurrency</SectionTitle>
              <FieldWrap>
                <FieldLabel>
                  DFY Concurrency Limit <span style={{ color: '#e53e3e' }}>*</span>
                </FieldLabel>
                <input
                  type='number'
                  className='form-control form-control-sm'
                  style={{ maxWidth: '200px' }}
                  value={dfyConcurrency}
                  min={0}
                  onChange={e => {
                    const v = e.target.value;
                    setDfyConcurrency(v);
                    setPassthrough(prev => ({
                      ...prev,
                      ea_extra: { ...prev.ea_extra, concurrency_value: v === '' ? 0 : Number(v) }
                    }));
                  }}
                />
                <HelpNote>Maximum DFY jobs in processing / in-progress / review at once.</HelpNote>
              </FieldWrap>
            </SectionBlock>

            <FormDivider />

            <DIYEntitlementSection
              form={diyForm}
              setForm={setDiyForm}
              diyRemaining={diyRemaining}
              handleTradeToggle={handleTradeToggle}
              email={email}
              licensePeriodResets={licenseCounters?.periodResets}
            />

            {/* Other configuration toggles */}
            <SectionBlock>
              <SectionTitle>Other configuration toggles </SectionTitle>

              {CHECKBOX_FIELDS_BEAM_USER.map(field => (
                <CheckboxRow key={field}>
                  <input
                    type='checkbox'
                    id={field}
                    checked={checkboxes[field]}
                    onChange={e => setCheckboxes(prev => ({ ...prev, [field]: e.target.checked }))}
                  />
                  <label htmlFor={field}>{formatBeamCheckboxLabel(field)}</label>
                </CheckboxRow>
              ))}

              <FieldWrap style={{ marginTop: '10px' }}>
                <FieldLabel>Billing type</FieldLabel>
                <select
                  className='form-select form-select-sm'
                  style={{ maxWidth: '200px' }}
                  value={billingType}
                  onChange={e => setBillingType(e.target.value)}>
                  <option value='manual'>Manual</option>
                  <option value='automated'>Automated</option>
                </select>
              </FieldWrap>
            </SectionBlock>

            {needsSuspendedEarlyAck && (
              <div className='flex items-start gap-3 p-3 mb-3 border border-amber-200 bg-amber-50 rounded-lg'>
                <Checkbox
                  checked={acknowledgeSuspendedEarly}
                  onChange={e => setAcknowledgeSuspendedEarly(e.target.checked)}
                />
                <Typography variant='body4' color='var(--color-neutral-80)' style={{ lineHeight: 1.45 }}>
                  Platform access ends before the contract end. Check this box to confirm suspended-early platform
                  access.
                </Typography>
              </div>
            )}

            {/* Save */}
            <SaveRow>
              <Button
                color='primary'
                variant='contained'
                onClick={handleSave}
                loading={saveLoading}
                disabled={!sopConfigured}>
                Save Changes
              </Button>
            </SaveRow>
          </>
        )}
      </CardWrap>
    </FormContainer>
  );
};

export default UpdateEnterpriseContractBeam;
